import { useState } from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { useLaborRates } from './hooks/useLaborRates';
import { permissionEngine } from '@/core/permissions';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Plus, Edit2, Trash2, Users } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LaborRateModal } from './components/LaborRateModal';
import type { UserLaborRate } from './types/laborRates.types';
import { format } from 'date-fns';

export function LaborRatesPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.rates.view');

  const { data, isLoading, deleteRate, isDeleting } = useLaborRates();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<UserLaborRate | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<UserLaborRate | null>(null);

  const handleEdit = (item: UserLaborRate) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(undefined);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteRate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const TYPE_LABELS: Record<string, string> = {
    hourly: 'Theo giờ',
    daily: 'Theo ngày (Công)',
    monthly: 'Khóan tháng',
    custom: 'Tùy chỉnh',
  };

  return (
    <PageLayout title="Đơn giá nhân công" description="Thiết lập định mức chi phí cho từng nhân sự">
      <div className="flex flex-col h-full gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Danh sách đơn giá</h3>
          <AuthButton
            module="inventory.rates"
            action="create"
            behavior="disable"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 rounded-md inline-flex items-center gap-2 sm:text-sm text-xs"
            onClick={handleAdd}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Thêm đơn giá</span>
          </AuthButton>
        </div>

        <div className="flex-1 bg-white border border-slate-200 rounded-md overflow-hidden min-h-0 flex flex-col">
          <div className="flex-1 overflow-auto min-h-0 relative">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Nhân sự</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32">Loại đơn giá</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-40 text-right">Mức giá (VNĐ)</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-40">Hiệu lực từ</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-24 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500 animate-pulse">Đang tải dữ liệu...</td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-12 text-center text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      Chưa có đơn giá nhân công nào được thiết lập.
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 sm:text-sm text-xs">{item.user?.full_name || 'Không xác định'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{item.user?.email || '-'}</div>
                      </td>
                      <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">
                        {TYPE_LABELS[item.rate_type] || item.rate_type}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-mono font-medium text-emerald-600 sm:text-sm text-xs">
                          {item.amount.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">
                        {item.effective_from ? format(new Date(item.effective_from), 'dd/MM/yyyy') : '-'}
                        {item.effective_to && <span className="text-slate-400"> đến {format(new Date(item.effective_to), 'dd/MM/yyyy')}</span>}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <AuthButton
                          module="inventory.rates"
                          action="update"
                          behavior="hide"
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </AuthButton>
                        <AuthButton
                          module="inventory.rates"
                          action="delete"
                          behavior="hide"
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </AuthButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <LaborRateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        rate={editingItem}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xác nhận xóa đơn giá"
        message={
          <span>
            Bạn có chắc chắn muốn xóa đơn giá của nhân sự <strong>{deleteTarget?.user?.full_name}</strong>?
          </span>
        }
        confirmText="Xóa đơn giá"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
}

export default LaborRatesPage;
